import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, ImagePlus, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const tok = () => "Bearer " + (localStorage.getItem("go_oil_token") || "");
const MANAGERS = ["owner", "super_admin", "company_admin", "platform_owner"];
const STATUS = { open: "Open", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };
const BADGE = { open: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", resolved: "bg-emerald-100 text-emerald-800", closed: "bg-slate-200 text-slate-700" };
const fmt = (s) => { try { return new Date(s).toLocaleString("en-IN"); } catch (e) { return s; } };

async function call(path, opts = {}) {
  const r = await fetch(`${BASE}/api/dms/tickets${path}`, { ...opts, headers: { Authorization: tok(), ...(opts.headers || {}) } });
  if (!r.ok) {
    let m = "Request failed";
    try { const j = await r.json(); if (j && typeof j.detail === "string") m = j.detail; } catch (e) { /* ignore */ }
    throw new Error(m);
  }
  return r;
}

async function shrink(file) {
  if (!file.type.startsWith("image/") || file.size < 700000) return file;
  try {
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s); c.height = Math.round(bmp.height * s);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.8));
    return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
  } catch (e) { return file; }
}

function Media({ file }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "", dead = false;
    call(`/file/${file.id}`).then((r) => r.blob()).then((b) => { if (!dead) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { dead = true; if (u) URL.revokeObjectURL(u); };
  }, [file.id]);
  if (!url) return <div className="text-xs text-slate-400">Loading...</div>;
  if (file.kind === "audio") return <audio controls src={url} className="w-full max-w-sm" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={file.name} className="h-32 rounded border object-cover" /></a>;
}

function Detail({ t, manager, onUpdate }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState(t.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { setStatus(t.status); setText(""); setErr(""); }, [t.id, t.status]);
  const send = async () => {
    if (!text.trim() && status === t.status) return;
    setBusy(true); setErr("");
    try {
      const r = await call(`/${t.id}/reply`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, status: manager ? status : undefined }) });
      onUpdate(await r.json()); setText("");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const files = t.files || [];
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="ticket-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold">{t.ticket_no} - {t.subject}</div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[t.status] || ""}`}>{STATUS[t.status] || t.status}</span>
      </div>
      <div className="text-xs text-slate-500">{t.created_by_name} ({t.created_by_role}) - {fmt(t.created_at)}</div>
      {t.description && <div className="whitespace-pre-wrap text-sm">{t.description}</div>}
      {files.length > 0 && <div className="flex flex-wrap gap-3">{files.map((f) => <Media key={f.id} file={f} />)}</div>}
      {(t.replies || []).length > 0 && (
        <div className="space-y-2 border-t pt-3">
          {t.replies.map((r, i) => (
            <div key={i} className="rounded bg-slate-50 p-2 text-sm">
              <div className="text-xs text-slate-500">{["owner", "owner_accountant", "super_admin"].includes(r.by_role) ? "Go Oil Assistant" : `${r.by_name} (${r.by_role})`} - {fmt(r.at)}</div>
              <div className="whitespace-pre-wrap">{r.text}</div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2 border-t pt-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Write a reply..."
          className="w-full rounded-md border border-slate-300 p-2 text-sm" />
        <div className="flex flex-wrap items-center gap-2">
          {manager && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="ticket-status">
              {Object.keys(STATUS).map((k) => <option key={k} value={k}>{STATUS[k]}</option>)}
            </select>
          )}
          <button type="button" onClick={send} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#a67c00] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {manager ? "Update" : "Send reply"}
          </button>
          {err && <span className="text-xs text-red-600">{err}</span>}
        </div>
      </div>
    </div>
  );
}

export function TicketsPage() {
  const auth = useAuth();
  const role = auth && auth.user ? auth.user.role : "";
  const manager = MANAGERS.includes(role);
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState("");
  const [filter, setFilter] = useState("");
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [images, setImages] = useState([]);
  const [audio, setAudio] = useState(null);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try { const r = await call(filter ? `?status=${filter}` : ""); setList(await r.json()); } catch (e) { setErr(e.message); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const stopRec = () => { const m = recRef.current; if (m && m.state !== "inactive") m.stop(); };
  useEffect(() => { if (recording && secs >= 120) stopRec(); }, [secs, recording]);
  useEffect(() => () => { stopRec(); clearInterval(timer.current); }, []);

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        setAudio({ blob, url: URL.createObjectURL(blob) });
        setRecording(false);
      };
      mr.start(); recRef.current = mr; setSecs(0); setRecording(true);
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (e) { setErr("Microphone not available or permission denied"); }
  };

  const addImages = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const room = 3 - images.length;
    const out = [];
    for (const f of picked.slice(0, Math.max(room, 0))) {
      const s = await shrink(f);
      out.push({ file: s, url: URL.createObjectURL(s) });
    }
    setImages((p) => [...p, ...out]);
  };

  const submit = async () => {
    if (!desc.trim() && !audio && !images.length) { setErr("Write the problem, or attach an image / voice note"); return; }
    setBusy(true); setErr(""); setOk("");
    try {
      const fd = new FormData();
      fd.append("subject", subject);
      fd.append("description", desc);
      images.forEach((i) => fd.append("images", i.file));
      if (audio) {
        const mt = audio.blob.type || "";
        const ext = mt.includes("mp4") ? "m4a" : mt.includes("ogg") ? "ogg" : "webm";
        fd.append("audio", audio.blob, `voice.${ext}`);
      }
      const r = await call("", { method: "POST", body: fd });
      const t = await r.json();
      setOk(`Ticket ${t.ticket_no} raised`);
      setSubject(""); setDesc(""); setImages([]); setAudio(null);
      load();
    } catch (e) { setErr(e.message || "Could not raise ticket"); } finally { setBusy(false); }
  };

  const sel = list.find((t) => t.id === selId);
  const onUpdate = (t) => setList((l) => l.map((x) => (x.id === t.id ? t : x)));
  const mm = String(Math.floor(secs / 60)).padStart(2, "0") + ":" + String(secs % 60).padStart(2, "0");

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{manager ? "Support Tickets" : "Raise a Ticket"}</h1>
        <p className="text-sm text-slate-500">{manager ? "Problems reported by all logins." : "Describe your problem. You can attach photos or record a voice note."}</p>
      </div>

      {!manager && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-5">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Write your problem here..."
            className="w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="ticket-desc" />
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">
              <ImagePlus size={16} /> Attach image ({images.length}/3)
              <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} disabled={images.length >= 3} />
            </label>
            {!recording ? (
              <button type="button" onClick={startRec} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50" data-testid="rec-start">
                <Mic size={16} /> Record voice
              </button>
            ) : (
              <button type="button" onClick={stopRec} className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-2 text-sm text-white" data-testid="rec-stop">
                <Square size={14} /> Stop ({mm})
              </button>
            )}
          </div>
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {images.map((i, idx) => (
                <div key={idx} className="relative">
                  <img src={i.url} alt="" className="h-20 w-20 rounded border object-cover" />
                  <button type="button" onClick={() => setImages((p) => p.filter((_, k) => k !== idx))}
                    className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
          {audio && (
            <div className="flex items-center gap-2">
              <audio controls src={audio.url} className="max-w-xs" />
              <button type="button" onClick={() => setAudio(null)} className="rounded p-1 hover:bg-slate-100"><Trash2 size={16} /></button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button type="button" onClick={submit} disabled={busy || recording}
              className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#c9a227] to-[#a67c00] px-4 py-2 text-sm font-medium text-white disabled:opacity-60" data-testid="ticket-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {busy ? "Sending..." : "Submit Ticket"}
            </button>
            {ok && <span className="text-sm text-emerald-700">{ok}</span>}
          </div>
        </div>
      )}
      {err && <div className="text-sm text-red-600">{err}</div>}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">{manager ? "All Tickets" : "My Tickets"} ({list.length})</div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">All status</option>
            {Object.keys(STATUS).map((k) => <option key={k} value={k}>{STATUS[k]}</option>)}
          </select>
        </div>
        <div className="divide-y rounded-lg border border-slate-200 bg-white">
          {list.length === 0 && <div className="p-4 text-sm text-slate-500">No tickets.</div>}
          {list.map((t) => (
            <button key={t.id} type="button" onClick={() => setSelId(t.id === selId ? "" : t.id)}
              className={`flex w-full items-center justify-between gap-3 p-3 text-left text-sm hover:bg-amber-50 ${t.id === selId ? "bg-amber-50" : ""}`}>
              <div className="min-w-0">
                <div className="truncate font-medium">{t.ticket_no} - {t.subject}</div>
                <div className="text-xs text-slate-500">{manager ? `${t.created_by_name} (${t.created_by_role}) - ` : ""}{fmt(t.created_at)}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[t.status] || ""}`}>{STATUS[t.status] || t.status}</span>
            </button>
          ))}
        </div>
        {sel && <Detail t={sel} manager={manager} onUpdate={onUpdate} />}
      </div>
    </div>
  );
}
